"use client";

import AdDetailModel from "@/models/AdDetailModel";
import { Avatar, Box, Button, Typography } from "@mui/material";
import PhoneIcon from "@mui/icons-material/Phone";
import { useState } from "react";
import { TOP_NAV_HEIGHT_DESKTOP_NUMBER } from "@/app/layout/GlobalNav";
import {
  AD_DETAILS_BORDER_RADIUS,
  BOX_SHADOW_SIZE_LARGE,
} from "@/app/layout/LayoutConstants";
import dynamic from "next/dynamic";
import { SEARCH_FILTER_BAR_HEIGHT } from "@/app/layout/PageBody";
import PersonIcon from "@mui/icons-material/Person";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { AD_DETAILS_DESKTOP_SPACING } from "./AdDetailsPageLayout";
import { useTopNavHeadroom } from "@/app/layout/useTopNavHeadroom";
import { RevealAdPhone } from "@/actions/details/RevealAdPhone";
import { useRecaptcha } from "@/app/layout/useRecaptcha";
import VerifiedIcon from "@mui/icons-material/Verified";
import { DETAILS_ADS } from "../ads";
import AdModule from "@/components/shared/AdModule";
import { CreateAnalyticEvent } from "@/actions/details/CreateAnalyticEvent";

// AJB: source of jank?  Maybe allocate vertical space?
const AdEmailEnquiry = dynamic(() => import("./AdEmailEnquiry"), {
  ssr: false,
});

export default function AdAside({
  ad,
  innerRef,
}: {
  ad: AdDetailModel;
  innerRef: React.RefObject<HTMLDivElement | null>;
}) {
  const [sellerPhoneIsRevealing, setSellerPhoneIsRevealing] = useState(false);
  const [sellerPhoneRevealed, setSellerPhoneRevealed] = useState<
    { local: string; e164: string } | undefined
  >();
  const { isCollapsed } = useTopNavHeadroom();
  const { getRecaptchaToken } = useRecaptcha();

  return (
    <Box
      sx={(theme) => ({
        position: "sticky",
        top:
          TOP_NAV_HEIGHT_DESKTOP_NUMBER +
          (isCollapsed ? 0 : SEARCH_FILTER_BAR_HEIGHT) +
          Number(theme.spacing(4).replace("px", "")) +
          "px", // AJB: got to be a cleaner way
        transition: theme.transitions.create("top", {
          duration: theme.transitions.duration.standard,
          easing: theme.transitions.easing.easeInOut,
        }),
      })}
    >
      {!ad.isInactive && (
        <Box
          ref={innerRef}
          sx={{
            display: "flex",
            flexDirection: "column",
            backgroundColor: "white",
            borderRadius: AD_DETAILS_BORDER_RADIUS,
            boxShadow: BOX_SHADOW_SIZE_LARGE,
            width: "100%",
            overflow: "hidden",
            marginBottom: AD_DETAILS_DESKTOP_SPACING,
            alignSelf: "start",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: ad.sellerIsVerified ? "column" : "row",
              alignItems: "center",
              gap: 2,
              padding: 2,
              borderBottom: "1px solid",
              borderColor: "divider",
              backgroundColor: "grey.100",
            }}
          >
            {ad.sellerIsVerified ? (
              <Box
                component={"a"}
                href={`/member/${ad.sellerId}`}
                sx={{
                  position: "relative",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  width: "100%",
                }}
              >
                <Avatar
                  src={ad.sellerPictureSrc}
                  sx={{
                    width: 120,
                    height: 120,
                    backgroundColor: "white",
                  }}
                >
                  <PersonIcon
                    sx={{
                      fontSize: "40px",
                      color: "grey.500",
                    }}
                  />
                </Avatar>
                <Box
                  sx={{
                    position: "absolute",
                    right: 0,
                  }}
                >
                  <NavigateNextIcon
                    sx={{
                      fontSize: "50px",
                    }}
                  />
                </Box>
              </Box>
            ) : (
              <Avatar
                src={ad.sellerPictureSrc}
                sx={{
                  width: 60,
                  height: 60,
                  backgroundColor: "white",
                }}
              >
                <PersonIcon
                  sx={{
                    fontSize: "40px",
                    color: "grey.500",
                  }}
                />
              </Avatar>
            )}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: ad.sellerIsVerified ? 1 : 0.5,
                alignItems: ad.sellerIsVerified ? "center" : "initial",
              }}
            >
              <Typography variant="h5">
                {ad.sellerName || "Horsezone Seller"}
              </Typography>
              {/* AJB: get this data */}
              <Box
                sx={
                  ad.sellerIsVerified
                    ? {
                        display: "flex",
                        gap: 2,
                        alignItems: "center",
                        justifyContent: "center",
                      }
                    : {
                        display: "flex",
                        gap: 1,
                      }
                }
              >
                <Typography variant="body2">
                  {ad.sellerIsVerified ? (
                    <span>
                      {ad.sellerTotalAds} listings since {ad.sellerMemberSince}
                    </span>
                  ) : (
                    <>
                      <span>Member since {ad.sellerMemberSince}</span>
                      {ad.sellerTotalAds > 0 && (
                        <>
                          <span>&nbsp;&nbsp;·&nbsp;&nbsp;</span>
                          <span>
                            {ad.sellerTotalAds} ad
                            {ad.sellerTotalAds !== 1 ? "s" : ""}
                          </span>
                        </>
                      )}
                    </>
                  )}
                </Typography>
                {ad.website && (
                  <>
                    <Typography variant="body2">·</Typography>
                    <a href={ad.website} target="_blank" rel="noreferrer">
                      <Typography
                        variant="body2"
                        sx={{
                          textDecoration: "underline",
                          display: "flex",
                          gap: 0.5,
                          alignItems: "center",
                        }}
                      >
                        {ad.sellerWebsiteLabel || "Website"}
                        <OpenInNewIcon sx={{ height: "14px" }} />
                      </Typography>
                    </a>
                  </>
                )}
              </Box>

              {ad.sellerIsVerified && (
                <Box
                  sx={{
                    display: "flex",
                    gap: 0.5,
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <VerifiedIcon
                    sx={{
                      color: "#1d9bf0",
                    }}
                  />
                  <Typography variant="caption">Verified Member</Typography>
                </Box>
              )}
            </Box>
            {!ad.sellerIsVerified && (
              <Box
                sx={{
                  marginLeft: "auto",
                }}
              >
                <a href={`/member/${ad.sellerId}`}>
                  <NavigateNextIcon
                    sx={{
                      fontSize: "50px",
                    }}
                  />
                </a>
              </Box>
            )}
          </Box>

          <AdEmailEnquiry ad={ad} />

          {ad.sellerPhoneMasked && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 2,
                padding: 3,
                backgroundColor: "grey.100",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  gap: 2,
                  flexDirection: "column",
                  alignItems: "center",
                  textAlign: "center",
                }}
              >
                {sellerPhoneRevealed && (
                  <Typography>
                    Tell <strong>{ad.sellerName}</strong> you saw it on{" "}
                    <strong>Horsezone</strong>!
                  </Typography>
                )}

                <Box
                  sx={{
                    display: "flex",
                    gap: 2,
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <PhoneIcon /> Call
                  </Typography>

                  {sellerPhoneRevealed ? (
                    <Typography
                      component="a"
                      href={`tel:${sellerPhoneRevealed.e164}`}
                      sx={{ textDecoration: "underline" }}
                      onClick={async () => {
                        await CreateAnalyticEvent({
                          event: {
                            adIds: [ad.id],
                            eventType: "AD#ENGAGEMENT#PHONE#CLICK",
                          },
                        });
                      }}
                    >
                      {sellerPhoneRevealed.local}
                    </Typography>
                  ) : (
                    <>
                      <Typography>{ad.sellerPhoneMasked}</Typography>
                      <Button
                        sx={{
                          fontWeight: "bold",
                          cursor: "pointer",
                          textDecoration: "underline",
                          color: "black",
                        }}
                        onClick={async () => {
                          setSellerPhoneIsRevealing(true);
                          const fullSellerPhone = await RevealAdPhone({
                            adId: ad.id,
                            recaptchaToken: await getRecaptchaToken(),
                          });
                          setSellerPhoneRevealed(fullSellerPhone);

                          await CreateAnalyticEvent({
                            event: {
                              adIds: [ad.id],
                              eventType: "AD#ENGAGEMENT#PHONE#REVEAL",
                            },
                          });
                        }}
                        loading={sellerPhoneIsRevealing}
                      >
                        Show
                      </Button>
                    </>
                  )}
                </Box>
              </Box>
            </Box>
          )}
        </Box>
      )}

      {!ad.sellerIsVerified && (
        <AdModule config={DETAILS_ADS.Horsezone_Details_Side} />
      )}
    </Box>
  );
}
